import { SelectColor } from "@/components/elements/SelectColor";
import { blue, divider, text } from "@/theme/colors";
import { elevation, rounded } from "@/theme/variables";
import { ATTACHED_DOCUMENT_COLOR } from "@/types/enum";
import AddCircleOutlineIcon from "@mui/icons-material/AddCircleOutline";
import { Box, Popover, Stack, Typography } from "@mui/material";
import { useCallback, useEffect, useRef, useState } from "react";

const OVERFLOW_BADGE_WIDTH = 32;
const ADD_BUTTON_WIDTH = 26;
const TAG_GAP = 4;
export const MAX_DOCUMENT_TYPES = 4;
// Outer overflow Popover uses zIndex 1600 to layer above MUI Modal (default 1300)
// when this form is rendered inside TalentInfoModal. The inner Select dropdown
// must sit above the outer Popover to be visible and clickable.
const OVERFLOW_POPOVER_Z_INDEX = 1600;
const OVERFLOW_INNER_MENU_Z_INDEX = OVERFLOW_POPOVER_Z_INDEX + 100;

type DocumentTypeOption = {
  value: string | number;
  label: string;
  color?: string;
  hidden?: boolean;
};

type DocumentTypeTagsRowProps = {
  typeIds: string[];
  attachmentDocumentTypeOptions: DocumentTypeOption[];
  onTypeChange: (typeIndex: number, newValue: string) => void;
  onAddType: () => void;
};

export const DocumentTypeTagsRow = ({ typeIds, attachmentDocumentTypeOptions, onTypeChange, onAddType }: DocumentTypeTagsRowProps) => {
  const containerRef = useRef<HTMLDivElement>(null);
  const measureRef = useRef<HTMLDivElement>(null);
  const [maxVisible, setMaxVisible] = useState(typeIds.length);
  const [overflowAnchorEl, setOverflowAnchorEl] = useState<HTMLElement | null>(null);
  const isOverflowOpen = Boolean(overflowAnchorEl);

  // Measure actual tag widths and calculate how many fit
  useEffect(() => {
    const container = containerRef.current;
    const measureContainer = measureRef.current;
    if (!container || !measureContainer) return;

    const calculate = () => {
      const availableWidth = container.offsetWidth;
      if (availableWidth === 0 || typeIds.length === 0) {
        setMaxVisible(typeIds.length);
        return;
      }

      const tagEls = measureContainer.querySelectorAll("[data-measure-tag]");
      let usedWidth = ADD_BUTTON_WIDTH;
      let fitCount = 0;

      for (let i = 0; i < tagEls.length; i++) {
        const tagWidth = (tagEls[i] as HTMLElement).offsetWidth + TAG_GAP;
        const hasMore = i < typeIds.length - 1;
        const extra = hasMore ? OVERFLOW_BADGE_WIDTH + TAG_GAP : 0;

        if (usedWidth + tagWidth + extra <= availableWidth) {
          usedWidth += tagWidth;
          fitCount++;
        } else {
          break;
        }
      }

      setMaxVisible(Math.max(fitCount, 1));
    };

    requestAnimationFrame(calculate);

    const observer = new ResizeObserver(calculate);
    observer.observe(container);
    return () => observer.disconnect();
  }, [typeIds]);

  const visibleTypes = typeIds.slice(0, maxVisible);
  const overflowTypes = typeIds.slice(maxVisible);
  const overflowCount = overflowTypes.length;

  const handleOverflowClick = useCallback((e: React.MouseEvent<HTMLElement>) => {
    e.stopPropagation();
    setOverflowAnchorEl(e.currentTarget);
  }, []);

  const handleOverflowClose = useCallback(() => {
    setOverflowAnchorEl(null);
  }, []);

  return (
    <>
      {/* Hidden measurement container — renders ALL tags to measure their widths */}
      <Box
        ref={measureRef}
        sx={{
          position: "absolute",
          visibility: "hidden",
          pointerEvents: "none",
          display: "flex",
          gap: `${TAG_GAP}px`,
          whiteSpace: "nowrap",
        }}
      >
        {typeIds.map((typeId, typeIndex) => (
          <Box key={typeIndex} data-measure-tag sx={{ flexShrink: 0 }}>
            <SelectColor
              variant="pill"
              options={attachmentDocumentTypeOptions}
              optionColors={ATTACHED_DOCUMENT_COLOR}
              value={typeId}
              onChange={() => {}}
              nonControl
              clearable
              sx={{
                width: "fit-content",
                "& .MuiOutlinedInput-notchedOutline": { border: "none" },
              }}
            />
          </Box>
        ))}
      </Box>

      {/* Visible tags */}
      <Stack ref={containerRef} direction="row" alignItems="center" justifyContent="end" spacing={0.5} sx={{ flex: 1, minWidth: 0 }}>
        {visibleTypes.map((typeId, typeIndex) => (
          <SelectColor
            key={typeIndex}
            variant="pill"
            options={attachmentDocumentTypeOptions}
            optionColors={ATTACHED_DOCUMENT_COLOR}
            value={typeId}
            onChange={(newValue) => onTypeChange(typeIndex, newValue)}
            nonControl
            clearable
            sx={{
              width: "fit-content",
              "& .MuiOutlinedInput-notchedOutline": { border: "none" },
            }}
          />
        ))}
        {overflowCount > 0 && (
          <Typography
            variant="body14"
            sx={{
              color: text.primary,
              textDecoration: "underline",
              cursor: "pointer",
              whiteSpace: "nowrap",
              flexShrink: 0,
            }}
            onClick={handleOverflowClick}
          >
            +{overflowCount}
          </Typography>
        )}
        <Box display="flex" alignItems="center" sx={{ height: "32px", flexShrink: 0 }}>
          <AddCircleOutlineIcon
            sx={{
              color: typeIds.length >= MAX_DOCUMENT_TYPES ? text.tertiary : blue[80],
              fontSize: 18,
              cursor: typeIds.length >= MAX_DOCUMENT_TYPES ? "default" : "pointer",
              opacity: typeIds.length >= MAX_DOCUMENT_TYPES ? 0.4 : 1,
            }}
            onClick={typeIds.length >= MAX_DOCUMENT_TYPES ? undefined : onAddType}
          />
        </Box>
      </Stack>
      <Popover
        open={isOverflowOpen}
        anchorEl={overflowAnchorEl}
        onClose={handleOverflowClose}
        anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
        transformOrigin={{ vertical: "top", horizontal: "right" }}
        sx={{ zIndex: OVERFLOW_POPOVER_Z_INDEX, mt: 0.5 }}
        slotProps={{
          paper: {
            sx: {
              boxShadow: elevation.lv2,
              border: `1px solid ${divider.middle}`,
              borderRadius: rounded.xs,
              p: 1,
            },
          },
        }}
      >
        <Stack direction="row" spacing={0.5} flexWrap="wrap" useFlexGap>
          {overflowTypes.map((typeId, i) => {
            const actualIndex = maxVisible + i;
            return (
              <SelectColor
                key={actualIndex}
                variant="pill"
                options={attachmentDocumentTypeOptions}
                optionColors={ATTACHED_DOCUMENT_COLOR}
                value={typeId}
                onChange={(newValue) => onTypeChange(actualIndex, newValue)}
                nonControl
                clearable
                MenuProps={{ sx: { zIndex: OVERFLOW_INNER_MENU_Z_INDEX } }}
                sx={{
                  width: "fit-content",
                  "& .MuiOutlinedInput-notchedOutline": { border: "none" },
                }}
              />
            );
          })}
        </Stack>
      </Popover>
    </>
  );
};
